import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from './search';
import { pageParamKey, type PageNamespace } from './pageParamKeys';

/** URL-owned page. With a `namespace` the table keeps its own `${namespace}_page`
 *  so several paged tables can share one view; without one it is the plain `?page`. */
export function useCampaignPageParam(namespace?: PageNamespace): [number, (p: number) => void] {
  const navigate = useNavigate();
  const key = pageParamKey(namespace);
  const search = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const raw = search[key];
  const current = raw && raw > 0 ? raw : 1;
  const setPage = useCallback(
    (next: number) =>
      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({ ...p, [key]: next <= 1 ? undefined : next })),
        // Paging is an in-place update of the SAME view — keep the reader where
        // they are. The router's scrollRestoration:true otherwise treats each
        // ?page= change as a new location with no saved position and yanks the
        // window back to the top, re-revealing the KPIs the user scrolled past.
        resetScroll: false,
      } as never),
    [navigate, key],
  );
  return [current, setPage];
}
